<template>
  <div
    :class="['alert-modal-overlay', { show: show }]"
    v-show="show"
    @click.self="handleOverlayClick"
  >
    <div class="alert-modal-container" :class="[type, { 'modal-enter-active': show }]">
      <div class="alert-modal-content">
        <!-- Header -->
        <div class="alert-header">
          <div class="alert-icon">
            <i :class="getIcon()"></i>
          </div>
          <div class="alert-title-content">
            <h3 class="alert-title">{{ title }}</h3>
            <div class="alert-subtitle" v-if="subtitle">{{ subtitle }}</div>
          </div>
          <button
            v-if="dismissable"
            @click="close"
            type="button"
            class="close-btn"
            aria-label="Close"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="alert-body">
          <div class="alert-message">
            <slot>
              {{ message }}
            </slot>
          </div>
        </div>

        <!-- Footer -->
        <div class="alert-footer">
          <div class="alert-actions">
            <button
              v-if="showCancelButton"
              @click="handleCancel"
              class="btn btn-secondary"
            >
              {{ cancelButtonText }}
            </button>
            <button @click="handleConfirm" class="btn btn-primary" :class="type">
              {{ confirmButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch } from "vue";

// Props
// eslint-disable-next-line
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  type: {
    type: String,
    default: "info", // 'success', 'error', 'warning', 'info'
    validator: (value) => ["success", "error", "warning", "info"].includes(value),
  },
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  message: {
    type: String,
    default: "",
  },
  dismissable: {
    type: Boolean,
    default: true,
  },
  showCancelButton: {
    type: Boolean,
    default: false,
  },
  confirmButtonText: {
    type: String,
    default: "OK",
  },
  cancelButtonText: {
    type: String,
    default: "Cancel",
  },
  autoClose: {
    type: Boolean,
    default: false,
  },
  autoCloseDelay: {
    type: Number,
    default: 3000,
  },
  closeOnOverlay: {
    type: Boolean,
    default: true,
  },
});

// Emits
// eslint-disable-next-line
const emit = defineEmits(["close", "confirm", "cancel"]);

// Methods
const getIcon = () => {
  const icons = {
    success: "bi bi-check-circle-fill",
    error: "bi bi-exclamation-circle-fill",
    warning: "bi bi-exclamation-triangle-fill",
    info: "bi bi-info-circle-fill",
  };
  return icons[props.type] || icons.info;
};

const close = () => {
  emit("close");
};

const handleConfirm = () => {
  emit("confirm");
  if (!props.showCancelButton) {
    close();
  }
};

const handleCancel = () => {
  emit("cancel");
  close();
};

const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    close();
  }
};

// Auto close functionality
watch(
  () => props.show,
  (newVal) => {
    if (newVal && props.autoClose) {
      setTimeout(() => {
        close();
      }, props.autoCloseDelay);
    }
  }
);
</script>

<style scoped>
/* Alert Modal Overlay */
.alert-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.alert-modal-overlay.show {
  opacity: 1;
  visibility: visible;
}

/* Alert Modal Container */
.alert-modal-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.9);
  width: 90%;
  max-width: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.alert-modal-container.modal-enter-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Type-specific styles */
.alert-modal-container.success {
  border-top: 4px solid #28a745;
}

.alert-modal-container.error {
  border-top: 4px solid #dc3545;
}

.alert-modal-container.warning {
  border-top: 4px solid #ffc107;
}

.alert-modal-container.info {
  border-top: 4px solid #17a2b8;
}

.alert-modal-content {
  display: flex;
  flex-direction: column;
}

/* Header */
.alert-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
  position: relative;
}

.alert-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.alert-modal-container.success .alert-icon {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.alert-modal-container.error .alert-icon {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.alert-modal-container.warning .alert-icon {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.alert-modal-container.info .alert-icon {
  background: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
}

.alert-title-content {
  flex: 1;
  min-width: 0;
}

.alert-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
}

.alert-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.15);
  color: #495057;
}

/* Body */
.alert-body {
  padding: 0 1.5rem;
}

.alert-message {
  color: #495057;
  line-height: 1.5;
  font-size: 0.95rem;
}

/* Footer */
.alert-footer {
  padding: 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.alert-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  min-width: 80px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.btn-primary {
  color: white;
}

.alert-modal-container.success .btn-primary {
  background: linear-gradient(135deg, #28a745, #218838);
}

.alert-modal-container.error .btn-primary {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.alert-modal-container.warning .btn-primary {
  background: linear-gradient(135deg, #ffc107, #e0a800);
}

.alert-modal-container.info .btn-primary {
  background: linear-gradient(135deg, #17a2b8, #138496);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Responsive */
@media (max-width: 768px) {
  .alert-modal-container {
    width: 95%;
    margin: 1rem;
  }

  .alert-header {
    padding: 1.25rem 1.25rem 0.75rem;
  }

  .alert-body {
    padding: 0 1.25rem;
  }

  .alert-footer {
    padding: 1.25rem;
  }

  .alert-actions {
    flex-direction: column;
  }

  .btn {
    min-width: auto;
    flex: 1;
  }
}

@media (max-width: 480px) {
  .alert-header {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .alert-icon {
    align-self: center;
  }
}
</style>
